// src/App.js

import React, { lazy } from "react";
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { Route, Routes } from "react-router-dom";
import LandingPage from "./Pages/LandingPage/LandingPage";
import SignUpPage from "./Pages/SignUp";
// import NavBar from "./Components/NavBar/NavBar"; // Import the NavBar component
import CompaniesPage from "./Pages/CompaniesPage";
import LibraryHub from "./Components/Library/LibraryHub";
import LoginPage from "./Pages/LoginPage";
import WithNavigationBar from "./Components/Navigation/WithNavigationBar";
import WithoutNavigationBar from "./Components/Navigation/WithoutNavigationBar";
import MyListings from "./Pages/MyBiz/MyListings/MyListings";
import { Box, Button, CircularProgress } from "@mui/material";
import WithSidebarAndNavigationBar from "./Components/Navigation/WithSidebarAndNavigationBar";
import PlaceholderPage from "./Pages/MyBiz/PlaceholderPage";
import MyConnections from "./Pages/MyBiz/MyConnections";
import BrowseListingsPage from "./Pages/BrowseListingsPage/BrowseListingsPage";
import ListingDetailsPage from "./Pages/ListingDetailsPage/ListingDetailsPage";
import MyPurchaseHistory from "./Pages/MyBiz/MyPurchaseHistory/MyPurchaseHistory";
import MyOrders from "./Pages/MyBiz/MyOrders/MyOrders";
import CartPage from "./Pages/CartPage";
import CheckoutPage from "./Pages/CheckoutPage";
import AboutUsPage from "./Pages/AboutUs";
import ContactUsPage from "./Pages/ContactUs";
import TermsPage from "./Pages/Terms";
import PrivacyPage from "./Pages/Privacy";
import VerifyEmail from "./Pages/VerifyEmail";
import OrderConfirmationPage from "./Pages/OrderConfirmationPage";
import EmbeddedAdminHome from "./Pages/Channel/EmbeddedAdminHome";
import Publishing from "./Pages/Channel/Publishing";
import LinkStore from "./Pages/Channel/LinkStore";
import EmbeddedNavMenu from "./Components/Channel/EmbeddedNavMenu";
import { useAppBridge } from "./Components/Shopify/ShopifyAppBridgeProvider";
import { useQuery } from "@apollo/client";
import { GET_SHOPIFY_CONNECTION_STATUS } from "./graphql/queries";

const InputsPage = lazy(() => import("./Components/Library/InputsPage"));
const NavigationPage = lazy(
  () => import("./Components/Library/NavigationPage"),
);
const LayoutPage = lazy(() => import("./Components/Library/LayoutPage"));
const SurfacesPage = lazy(() => import("./Components/Library/SurfacesPage"));
const FeedbackPage = lazy(() => import("./Components/Library/FeedbackPage"));
const DataDisplayPage = lazy(
  () => import("./Components/Library/DataDisplayPage"),
);
const UtilsPage = lazy(() => import("./Components/Library/UtilsPage"));

// const emptyImage1 = require("./assets/images/Empty1.png");
// const emptyImage2 = require("./assets/images/Empty2.png");
// const emptyImage3 = require("./assets/images/Empty3.png");

function App() {
  const { isEmbedded, isReady } = useAppBridge();

  // Block rendering until App Bridge finishes probing for the iframe/host.
  // Without this the marketplace landing page flashes for a beat before
  // EmbeddedShell (in Shopify admin) or the marketplace routes take over.
  // Marketplace visitors don't hit this branch — ShopifyAppBridgeProvider
  // initializes isReady=true synchronously when no embed signals are found.
  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // When loaded inside Shopify Admin, render the channel admin surface.
  // The marketplace web routes (browse/cart/checkout/etc.) stay reachable
  // only on mysharestock.org outside the embedded iframe.
  if (isEmbedded) {
    return <EmbeddedShell />;
  }

  return (
    <div className="App">
      <ToastContainer
        position="bottom-center"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        toastClassName={() =>
          "custom-toast"
        }
      />
      <div className="content">
        <Routes>
            {/* Routes with Navigation Bar */}
            <Route element={<WithNavigationBar />}>
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="/browse" element={<BrowseListingsPage />} />
              <Route path="/listing/:listingId" element={<ListingDetailsPage />} />
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path='/about' element={<AboutUsPage />} />
              <Route path='/contact' element={<ContactUsPage />} />
              <Route path='/terms' element={<TermsPage />} />
              <Route path='/privacy' element={<PrivacyPage />} />
              <Route path='/verify-email' element={<VerifyEmail />} />
              <Route path='/order-confirmation' element={<OrderConfirmationPage />} />

            </Route>

            {/* Routes with Top Navigation Bar and Sidebar Navigation */}
            <Route element={<WithSidebarAndNavigationBar />}>
              <Route path="/my-biz/my-listings" element={<MyListings />} />
              <Route
                path="/my-biz/orders"
                element={
                  <MyOrders />
                }
              />
              <Route
                path="/my-biz/purchases"
                element={
                  <MyPurchaseHistory />
                }
              />
              <Route
                path="/my-biz/inbox"
                element={
                  <PlaceholderPage
                    title="Inbox"
                    emptyStateText="Your inbox is empty."
                    emptyStateImage="/path/to/image.png"
                    actionButtons={
                      <Button variant="contained" color="primary">
                        Check Messages
                      </Button>
                    }
                  />
                }
              />
              <Route
                path="/my-biz/my-connections"
                element={
                  <MyConnections />
                }
              />
              <Route
                path="/my-biz/settings"
                element={
                  <PlaceholderPage
                    title="Settings"
                    emptyStateText="Adjust your settings here."
                    emptyStateImage="/path/to/image.png"
                    actionButtons={
                      <Button variant="contained" color="primary">
                        Change Settings
                      </Button>
                    }
                  />
                }
              />
            </Route>

            {/* Routes without Navigation Bar */}
            <Route element={<WithoutNavigationBar />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              {/* Add other routes as needed */}
            </Route>

            <Route path="/library" element={<LibraryHub />} />
            <Route path="/library/inputs" element={<InputsPage />} />
            <Route path="/library/navigation" element={<NavigationPage />} />
            <Route path="/library/layout" element={<LayoutPage />} />
            <Route path="/library/surfaces" element={<SurfacesPage />} />
            <Route path="/library/feedback" element={<FeedbackPage />} />
            <Route path="/library/data-display" element={<DataDisplayPage />} />
            <Route path="/library/utils" element={<UtilsPage />} />
          </Routes>
        </div>
    </div>
  );
}

// Embedded shell: gates the admin surfaces behind a connection-status check.
// If the shop has no company linked yet (or is linked to a different
// company), render LinkStore so the merchant can sign in / sign up / claim
// the connection before any other admin route is reachable.
//
// The connection-status query lives here and only here — LinkStore receives
// status, error, and refetch via props. A previous version of LinkStore ran
// its own useQuery for the same data; after a sign-out (which calls
// apolloClient.clearStore) the two observers could drift, leaving the
// merchant pinned to LinkStore even after a successful link.
const EmbeddedShell: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_SHOPIFY_CONNECTION_STATUS,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  if (loading && !data) {
    return (
      <div className="App App--embedded">
        <EmbeddedNavMenu />
      </div>
    );
  }

  const status = data?.shopifyConnectionStatus ?? null;
  // On error, fall through to LinkStore — it surfaces the error message and
  // is the only screen that doesn't require a working connection.
  const isLinkedToCurrent = !error && status?.linked && status?.linkedToCurrentCompany;

  return (
    <div className="App App--embedded">
      <EmbeddedNavMenu />
      <Routes>
        {isLinkedToCurrent ? (
          <>
            <Route
              path="/"
              element={
                <EmbeddedAdminHome
                  status={status}
                  refetchStatus={async () => {
                    await refetch();
                  }}
                />
              }
            />
            <Route path="/publishing" element={<Publishing />} />
            <Route
              path="*"
              element={
                <EmbeddedAdminHome
                  status={status}
                  refetchStatus={async () => {
                    await refetch();
                  }}
                />
              }
            />
          </>
        ) : (
          <Route
            path="*"
            element={
              <LinkStore
                status={status}
                statusErrorMessage={error?.message ?? null}
                refetchStatus={async () => {
                  await refetch();
                }}
              />
            }
          />
        )}
      </Routes>
    </div>
  );
};

export default App;
